import React from 'react';
import _ from 'lodash';
import {Loader} from 'semantic-ui-react';
import '../css/TableCard.less';
import {useState} from 'react';
import {runLink} from '../util/runhelpers';
import {useLoadFile} from '../util/requests';
import MessageMediaNotFound from './MessageMediaNotFound';
import {MediaCardProps} from './MediaCard';
import {JSONparseUserFile} from '../util/jsonnan';
import PanelError from './elements/PanelError';
import {TableCellValue, TableMetadata} from '../types/media';
import {labelComponent, useStepWithRollback} from '../util/media';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {saveMediaTableAsCSV} from '../util/csv';
import WBReactTable, {DataRow} from './WBReactTable';
import {Column} from 'react-table';
import makeComp from '../util/profiler';

function useTableCardData(props: MediaCardProps) {
  const {mediaKey, runSignature} = props;
  const {currentStep, stepData, failedToLoad, rollback} = useStepWithRollback(
    props
  );

  // If this is file-based media, we put the contents of the file here once
  // loaded
  const [fileTableData, setFileTableData] = useState();
  const [error, setError] = useState<string | undefined>();

  let stepMedia = stepData != null ? stepData[mediaKey] : null;
  if (
    stepMedia != null &&
    stepMedia._type !== 'table-file' &&
    stepMedia._type !== 'table'
  ) {
    stepMedia = null;
  }
  const mediaFilePath: string | null =
    stepMedia != null && stepMedia._type === 'table-file'
      ? stepMedia.path
      : null;
  const isFileBacked = mediaFilePath != null;

  const onSuccess = React.useCallback(async (file: File) => {
    const fileContents = await new Response(file).text();
    const parsedTable = JSONparseUserFile(fileContents);
    if (parsedTable.error) {
      setError("Couldn't parse JSON data.");
    } else {
      setFileTableData({
        columns: parsedTable.result.columns
          .filter((col: any) => col != null)
          .map((col: any) => (col === '' ? ' ' : col)),
        data: parsedTable.result.data,
      } as any);
    }
  }, []);

  // Use empty string when we have null and skip the request
  // ! Warning: The callbacks passed to useLoadFile must stay
  // equal by reference or it will retrigger a new file download.
  const loading = useLoadFile(runSignature, mediaFilePath || '', {
    responseType: 'file',
    onSuccess,
    fallback: rollback,
    skip: !isFileBacked || mediaFilePath == null || failedToLoad,
  });

  const tableData: TableMetadata | null = isFileBacked
    ? fileTableData
    : stepMedia;

  return {
    tableData,
    loading,
    currentStep,
    error,
  };
}

const TableCard: React.FunctionComponent<MediaCardProps> = makeComp(
  props => {
    const {run, mediaKey, mediaIndex, runSignature, width, height} = props;

    const {tableData, loading, currentStep, error} = useTableCardData(props);

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    const tableDataRows: DataRow[] = [];
    let tableColumns: Column[] = [];
    if (tableData != null) {
      if (tableData.data != null && tableData.data.length > 0) {
        const pushRow = (row: TableCellValue[]) => {
          // Narrow the typing to what is supported in ReactTable
          const simplified = row.map(c => {
            return (typeof c === 'string' || typeof c === 'number'
              ? c
              : JSON.stringify(c)) as string | number;
          });

          tableDataRows.push({
            searchString: simplified.join(' '),
            row: simplified,
          });
        };
        if (_.isArray(tableData.data[0])) {
          (tableData.data as TableCellValue[][]).forEach(pushRow);
        } else {
          pushRow(tableData.data as TableCellValue[]);
        }
      }

      tableColumns = tableData.columns
        .filter(colName => colName)
        .map((colName, i) => ({
          id: colName.toString(),
          Header: colName,
          accessor: r => r[i],
        }));
    }

    return (
      <div className="table-card content-card" style={{width: '100%'}}>
        {/* TITLE (Run Name or Description) */}
        {labelComponent(props, currentStep, titleLink)}
        {tableData != null && (
          <>
            <WBReactTable
              className="panel-table"
              data={tableDataRows}
              columns={tableColumns}
              pageSize={50}
              pageSizeOptions={[20, 50, 100]}
              sortable
              resizable
            />
            <LegacyWBIcon
              className="content-card__download"
              onClick={() => saveMediaTableAsCSV(tableData)}
              name="download"
            />
          </>
        )}
        {tableData == null && loading ? (
          <div style={{width, height}}>
            <Loader active />
          </div>
        ) : (
          tableData == null &&
          (error != null ? (
            <PanelError
              className="media-missing"
              message={<div>{error}</div>}
            />
          ) : (
            <MessageMediaNotFound
              mediaIndex={mediaIndex}
              mediaKey={mediaKey}
              stepIndex={currentStep}
              mediaType="table"
            />
          ))
        )}
      </div>
    );
  },
  {id: 'TableCard'}
);

export default TableCard;
